import { Cloud, CloudOff, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { useLibraryStore } from '@/stores/library.store';

interface SyncStatusProps {
  className?: string;
  showLabel?: boolean;
}

export function SyncStatus({ className = '', showLabel = true }: SyncStatusProps) {
  const { sync, isAuthenticated, triggerSync } = useLibraryStore();
  const { isSyncing, pendingCount, lastSyncTime, syncError } = sync;

  if (!isAuthenticated) {
    return (
      <div className={`flex items-center gap-2 text-sm text-slate-500 ${className}`}>
        <CloudOff className="h-4 w-4" />
        {showLabel && <span>Local only</span>}
      </div>
    );
  }

  const handleManualSync = () => {
    triggerSync();
  };

  // Determine status
  if (syncError) {
    return (
      <button
        onClick={handleManualSync}
        className={`flex items-center gap-2 text-sm text-red-600 hover:text-red-700 ${className}`}
        title={`Sync error: ${syncError}. Click to retry.`}
      >
        <AlertCircle className="h-4 w-4" />
        {showLabel && <span>Sync error</span>}
      </button>
    );
  }

  if (isSyncing) {
    return (
      <div className={`flex items-center gap-2 text-sm text-blue-600 ${className}`}>
        <RefreshCw className="h-4 w-4 animate-spin" />
        {showLabel && <span>Syncing...</span>}
      </div>
    );
  }

  if (pendingCount > 0) {
    return (
      <button
        onClick={handleManualSync}
        className={`flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700 ${className}`}
        title={`${pendingCount} pending changes. Click to sync now.`}
      >
        <Cloud className="h-4 w-4" />
        {showLabel && <span>{pendingCount} pending</span>}
      </button>
    );
  }

  // All synced
  const formattedTime = lastSyncTime
    ? new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
      }).format(lastSyncTime)
    : null;

  return (
    <button
      onClick={handleManualSync}
      className={`flex items-center gap-2 text-sm text-green-600 hover:text-green-700 ${className}`}
      title={lastSyncTime ? `Last synced at ${formattedTime}. Click to sync now.` : 'Click to sync now.'}
    >
      <Check className="h-4 w-4" />
      {showLabel && <span>Synced</span>}
    </button>
  );
}
